import React, { useState, useEffect } from "react";
import { Card, Tabs, Table, Button, Tag, Modal, Select, Descriptions, message, DatePicker } from "antd";
import { FileTextOutlined, AlertOutlined, CheckCircleOutlined, UserSwitchOutlined, EyeOutlined } from "@ant-design/icons";
import { debounce } from "lodash";
import useRequest from "../../hooks/useRequest";
import moment from "moment"; // For handling dates

const { Option } = Select;

const CalendarPage = () => {
  const [auditLogs, setAuditLogs] = useState([]);
  const [escalations, setEscalations] = useState([]);
  const [compliance, setCompliance] = useState([]);
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [selectedEscalation, setSelectedEscalation] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [selectedDueDate, setSelectedDueDate] = useState(null); // New state for due date
  const [auditLoading, setAuditLoading] = useState(false);
  const [escalationLoading, setEscalationLoading] = useState(false);
  const [complianceLoading, setComplianceLoading] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const { request } = useRequest();

  const fetchAuditLogs = async () => {
    setAuditLoading(true);
    try {
      await request({
        url: `/audit/audit_logs`,
        method: "GET",
        onSuccess: (response) => {
          setAuditLogs(
            response?.data?.map((log) => ({
              id: log.id,
              orderId: log.order_id__id,
              status: log.status_change,
              action: log.action,
              actor: log.actor_user__name,
              timestamp: new Date(log.timestamp).toLocaleString(),
              ip: log.ip_address || "N/A",
            }))
          );
        },
        onError: () => {
          message.error("Failed to fetch audit logs");
        },
      });
    } finally {
      setAuditLoading(false);
    }
  };

  const fetchEscalations = async (page = 1, pageSize = 10) => {
    setEscalationLoading(true);
    try {
      await request({
        url: `/audit/escalations?page_no=${page}&page_size=${pageSize}`,
        method: "GET",
        onSuccess: (response) => {
          setEscalations(
            response?.data?.map((esc) => ({
              caseId: esc.case_id,
              orderId: esc.linked_order_id__order_id,
              complainant: esc.complainant_type,
              complainantName: esc.complainant_user__name || "N/A",
              assigned: esc.assigned_staff__name || "Unassigned",
              assigned_staff_id: esc.assigned_staff__id,
              description: esc.description,
              resolution_remarks: esc.resolution_remarks || "N/A",
              status: esc.status,
              created: new Date(esc.created_at).toISOString().split("T")[0],
              created_at: esc.created_at,
              updated_at: esc.updated_at,
              resolved_at: esc.resolved_at,
              due_date: esc.due_date,
              is_overdue: esc.is_overdue,
            }))
          );
          setPagination({
            current: response.pagination?.current_page || page,
            pageSize: response.pagination?.page_size || pageSize,
            total: response.pagination?.total_count || 0,
          });
        },
        onError: () => {
          message.error("Failed to fetch escalations");
        },
      });
    } finally {
      setEscalationLoading(false);
    }
  };

  const fetchCompliance = async () => {
    setComplianceLoading(true);
    try {
      await request({
        url: `/audit/compliance_checklists`,
        method: "GET",
        onSuccess: (response) => {
          setCompliance(
            response?.data?.map((item) => ({
              vendor: item.vendor__name,
              service: item.service_type_compliance,
              licenseExpiry: item.license_expiry_date,
              certificate: item.certifications,
              insurance: item.insurance_compliance,
              status: item.status,
            }))
          );
        },
        onError: () => {
          message.error("Failed to fetch compliance data");
        },
      });
    } finally {
      setComplianceLoading(false);
    }
  };

  const fetchStaffList = async () => {
    try {
      await request({
        url: `/audit/staff_list`,
        method: "GET",
        onSuccess: (response) => {
          if (response.status) {
            setStaffList(response.data);
          } else {
            message.error("Failed to fetch staff list");
          }
        },
        onError: () => {
          message.error("Failed to fetch staff list");
        },
      });
    } catch (error) {
      message.error("Failed to fetch staff list");
    }
  };

  const handleAssignEscalation = async () => {
    if (!selectedStaff) {
      message.error("Please select a staff member to assign");
      return;
    }
    if (!selectedDueDate) {
      message.error("Please select a due date");
      return;
    }

    setEscalationLoading(true);
    try {
      await request({
        url: `/audit/escalations_update`,
        method: "PATCH",
        data: {
          case_id: selectedEscalation.caseId,
          assigned_staff_id: selectedStaff,
          due_date: selectedDueDate.format("YYYY-MM-DD HH:mm:ss"), // Format for backend
        },
        onSuccess: (response) => {
          if (response.status) {
            setEscalations(
              escalations.map((esc) =>
                esc.caseId === selectedEscalation.caseId
                  ? {
                      ...esc,
                      assigned: staffList.find((staff) => staff.id === selectedStaff)?.name || "Unassigned",
                      assigned_staff_id: selectedStaff,
                      due_date: selectedDueDate.toISOString(), // Update frontend state
                      is_overdue: selectedDueDate.isBefore(moment()) && esc.status !== "closed",
                    }
                  : esc
              )
            );
            setAssignModalVisible(false);
            setSelectedEscalation(null);
            setSelectedStaff(null);
            setSelectedDueDate(null);
            message.success("Escalation assigned successfully");
          } else {
            message.error(response.error || "Failed to assign escalation");
          }
        },
        onError: () => {
          message.error("Failed to assign escalation");
        },
      });
    } catch (error) {
      console.error("Error assigning escalation:", error);
      message.error("Failed to assign escalation");
    } finally {
      setEscalationLoading(false);
    }
  };

  const handleViewEscalation = (record) => {
    setSelectedEscalation(record);
    setViewModalVisible(true);
  };

  useEffect(() => {
    fetchAuditLogs();
    fetchEscalations();
    fetchCompliance();
    fetchStaffList();
  }, []);

  const debouncedFetchAuditLogs = debounce(fetchAuditLogs, 500);

  const auditColumns = [
    { title: "Order ID", dataIndex: "orderId", width: 150 },
    { title: "Status", dataIndex: "status", width: 120 },
    { title: "Action", dataIndex: "action", width: 150 },
    { title: "Actor", dataIndex: "actor", width: 150 },
    { title: "Timestamp", dataIndex: "timestamp", width: 200 },
    { title: "IP/Device", dataIndex: "ip", width: 150 },
  ];

  const escalationColumns = [
    { title: "Case ID", dataIndex: "caseId", width: 200 },
    { title: "Order ID", dataIndex: "orderId", width: 150 },
    { title: "Complainant", dataIndex: "complainant", width: 120 },
    { title: "Complainant Name", dataIndex: "complainantName", width: 150 },
    { title: "Assigned", dataIndex: "assigned", width: 150 },
    { title: "Description", dataIndex: "description", width: 200 },
    {
      title: "Status",
      dataIndex: "status",
      width: 120,
      render: (status) => (
        <Tag color={status === "Open" ? "red" : status === "In Progress" ? "orange" : "green"}>
          {status}
        </Tag>
      ),
    },
    { title: "Created", dataIndex: "created", width: 120 },
    {
      title: "Action",
      key: "action",
      width: 200,
      render: (_, record) => (
        <div style={{ display: "flex", gap: "8px" }}>
          <Button
            type="primary"
            icon={<UserSwitchOutlined />}
            onClick={() => {
              setSelectedEscalation(record);
              setSelectedStaff(record.assigned_staff_id || null);
              setSelectedDueDate(record.due_date ? moment(record.due_date) : null); // Pre-fill due date
              setAssignModalVisible(true);
            }}
          >
            Assign
          </Button>
          <Button
            type="default"
            icon={<EyeOutlined />}
            onClick={() => handleViewEscalation(record)}
          >
            View
          </Button>
        </div>
      ),
    },
  ];

  const complianceColumns = [
    { title: "Vendor", dataIndex: "vendor", width: 150 },
    { title: "Service", dataIndex: "service", width: 150 },
    { title: "License Expiry", dataIndex: "licenseExpiry", width: 150 },
    { title: "Certificate", dataIndex: "certificate", width: 150 },
    { title: "Insurance", dataIndex: "insurance", width: 150 },
    {
      title: "Status",
      dataIndex: "status",
      width: 120,
      render: (status) => (
        <Tag color={status === "Blocked" ? "red" : status === "Warning" ? "orange" : "green"}>
          {status}
        </Tag>
      ),
    },
  ];

  return (
    <Card title="Admin Compliance">
      <Tabs
        defaultActiveKey="escalations"
        className="main_new_tabss"
        items={[
          {
            key: "escalations",
            label: <span><AlertOutlined /> Escalations</span>,
            children: (
              <Table
                rowKey="caseId"
                columns={escalationColumns}
                dataSource={escalations}
                loading={escalationLoading}
                scroll={{ x: 1200, y: 400 }}
                pagination={{
                  current: pagination.current,
                  pageSize: pagination.pageSize,
                  total: pagination.total,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                  pageSizeOptions: ['10', '20', '50'],
                  onChange: (page, pageSize) => fetchEscalations(page, pageSize),
                }}
              />
            ),
          },
          {
            key: "compliance",
            label: <span><CheckCircleOutlined /> Compliance</span>,
            children: (
              <Table
                rowKey="vendor"
                columns={complianceColumns}
                dataSource={compliance}
                loading={complianceLoading}
                scroll={{ x: 900, y: 400 }}
              />
            ),
          },
        ]}
      />

      <Modal
        title="Assign Escalation"
        open={assignModalVisible}
        onCancel={() => {
          setAssignModalVisible(false);
          setSelectedEscalation(null);
          setSelectedStaff(null);
          setSelectedDueDate(null);
        }}
        onOk={handleAssignEscalation}
        confirmLoading={escalationLoading}
      >
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", marginBottom: 4 }}>
            Assign To <span style={{ color: "red" }}>*</span>
          </label>
          <Select
            value={selectedStaff}
            onChange={(value) => setSelectedStaff(value)}
            placeholder="Select Staff"
            style={{ width: "100%", marginBottom: 16 }}
            showSearch
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {staffList.map((staff) => (
              <Option key={staff.id} value={staff.id}>
                {staff.name}
              </Option>
            ))}
          </Select>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", marginBottom: 4 }}>
            Due Date <span style={{ color: "red" }}>*</span>
          </label>
          <DatePicker
            showTime
            value={selectedDueDate}
            onChange={(date) => setSelectedDueDate(date)}
            placeholder="Select Due Date"
            style={{ width: "100%" }}
            format="YYYY-MM-DD HH:mm:ss"
          />
        </div>
      </Modal>

      <Modal
        title="Escalation Details"
        open={viewModalVisible}
        onCancel={() => {
          setViewModalVisible(false);
          setSelectedEscalation(null);
        }}
        footer={[
          <Button key="close" onClick={() => setViewModalVisible(false)}>
            Close
          </Button>,
        ]}
        width={600}
      >
        {selectedEscalation && (
          <Descriptions bordered column={1}>
            <Descriptions.Item label="Case ID">{selectedEscalation.caseId}</Descriptions.Item>
            <Descriptions.Item label="Order ID">{selectedEscalation.orderId || "N/A"}</Descriptions.Item>
            <Descriptions.Item label="Complainant">{selectedEscalation.complainant}</Descriptions.Item>
            <Descriptions.Item label="Complainant Name">{selectedEscalation.complainantName}</Descriptions.Item>
            <Descriptions.Item label="Assigned">{selectedEscalation.assigned}</Descriptions.Item>
            <Descriptions.Item label="Description">{selectedEscalation.description}</Descriptions.Item>
            <Descriptions.Item label="Resolution Remarks">
              {selectedEscalation.resolution_remarks || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag
                color={
                  selectedEscalation.status === "Open"
                    ? "red"
                    : selectedEscalation.status === "In Progress"
                    ? "orange"
                    : "green"
                }
              >
                {selectedEscalation.status}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Created At">
              {selectedEscalation.created_at
                ? new Date(selectedEscalation.created_at).toLocaleString("en-US", {
                    year: "numeric",
                    month: "short",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: true,
                  })
                : "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Updated At">
              {selectedEscalation.updated_at
                ? new Date(selectedEscalation.updated_at).toLocaleString("en-US", {
                    year: "numeric",
                    month: "short",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: true,
                  })
                : "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Resolved At">
              {selectedEscalation.resolved_at
                ? new Date(selectedEscalation.resolved_at).toLocaleString("en-US", {
                    year: "numeric",
                    month: "short",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: true,
                  })
                : "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Due Date">
              {selectedEscalation.due_date
                ? new Date(selectedEscalation.due_date).toLocaleString("en-US", {
                    year: "numeric",
                    month: "short",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: true,
                  })
                : "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Overdue">
              {selectedEscalation.is_overdue ? "Yes" : "No"}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </Card>
  );
};

export default CalendarPage;